import { connect } from '@ardoq/rxbeach';
import { getWorkspaceModeViewSettings$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import {
  ArdoqId,
  APIComponentAttributes,
  APIFieldAttributes,
} from '@ardoq/api-types';
import { fieldOperations } from '@ardoq/core';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { components$ } from 'streams/components/components$';
import { context$ } from 'streams/context/context$';
import { fields$ } from 'streams/fields/fields$';
import { references$ } from 'streams/references/references$';
import { workspaceAndModel$ } from 'streams/workspaceAndModel$';
import { locale$ } from 'streams/locale$';
import { groupingRules$ } from 'streams/grouping/groupingRules$';
import { Locale } from '@ardoq/locale';
import { i18nLocale$ } from 'streams/i18nLocale$';
import type { I18nLocale } from '@ardoq/i18n';
import { classes, isTruthy } from '@ardoq/common-helpers';
import { LI_BACKGROUND_CLASS_NAME } from '@ardoq/global-consts';
import { RoadmapView } from './RoadmapView';
import {
  RoadmapViewSettings,
  RoadmapViewProps,
  VIEW_ID,
  Tree,
  TableCell,
  LegendItem,
  HierarchyEntity,
} from './types';
import { DropdownItem } from '@ardoq/dropdown-menu';
import { getFieldOptionsForRoadmap } from './fieldUtils';
import { toScopeData } from './toScopeData';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { buildHierarchyFromScopeData } from './buildHierarchyFromScopeData';
import { isComponentEntity } from './types';
import {
  extractHierarchyFromGraph,
  getNodes,
} from './extractHierarchyFromGraph';
import { getRightMenuConfig } from 'viewSettings/getRightMenuConfig';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import {
  enhancedScopeDataOperations,
  entityCssOperations,
  scopeDataOperations,
} from '@ardoq/scope-data';
import { groupBy } from 'lodash';

const getComponentCSS = (component: APIComponentAttributes): string => {
  const entityClassName = entityCssOperations.getEntityCSSKey(component);
  const typeId = component.typeId;

  return classes(
    'component',
    entityClassName,
    typeId,
    LI_BACKGROUND_CLASS_NAME
  );
};

const getChildComponents = (
  parentItem: Tree<HierarchyEntity>
): APIComponentAttributes[] => {
  return parentItem.children
    .filter(child => isComponentEntity(child.data))
    .map(child => child.data.data) as APIComponentAttributes[];
};

const buildTableRow = (
  parentItem: Tree<HierarchyEntity>,
  fieldValues: string[],
  selectedField: APIFieldAttributes,
  enhancedScopeData: EnhancedScopeData
): TableCell[] | null => {
  const parentName = parentItem.data.data.name;
  const children = getChildComponents(parentItem);

  const childrenByFieldValue = groupBy(
    children,
    child => child[selectedField.name]
  );

  const hasChildrenWithFieldValues = Object.values(childrenByFieldValue).some(
    childList => childList.length > 0
  );

  if (!hasChildrenWithFieldValues) {
    return null;
  }

  const cells: TableCell[] = [
    { content: parentName },
    ...fieldValues.map(fieldValue => {
      const childrenInColumn = childrenByFieldValue[fieldValue];
      if (childrenInColumn) {
        const components = childrenInColumn.map(child => ({
          content: child.name,
          cssClass: getComponentCSS(child),
          componentId: child._id,
          representationData:
            enhancedScopeDataOperations.getComponentRepresentationData(
              enhancedScopeData,
              child._id
            ),
        }));
        return { components };
      }
      return { content: '' };
    }),
  ];

  return cells;
};

const buildLegendItems = (
  children: APIComponentAttributes[],
  enhancedScopeData: EnhancedScopeData
): LegendItem[] => {
  return Object.values(groupBy(children, child => child.typeId)).map(
    componentsOfType => {
      const child = componentsOfType[0];
      const componentType =
        enhancedScopeDataOperations.getComponentTypeByModelIdAndTypeId(
          enhancedScopeData,
          child.model,
          child.typeId
        );
      const typeName = componentType?.name || child.typeId;

      return {
        id: child.typeId,
        name: typeName,
        cssClass: getComponentCSS(child),
        representationData:
          enhancedScopeDataOperations.getComponentRepresentationData(
            enhancedScopeData,
            child._id
          ),
      };
    }
  );
};

const emptyProps = (
  fieldOptions: DropdownItem[],
  viewSettings: RoadmapViewSettings,
  i18nLocale: I18nLocale
): RoadmapViewProps => ({
  selectedFieldName: viewSettings.selectedFieldName,
  selectedFieldLabel: undefined,
  selectedFieldValues: [],
  fieldOptions,
  tableRows: [],
  legend: [],
  rightMenu: getRightMenuConfig({
    isViewpointMode: false,
    viewId: VIEW_ID,
    viewstate: { selectedFieldName: viewSettings.selectedFieldName },
    exports: {},
    withLegend: false,
    knowledgeBaseLink: KnowledgeBaseLink.ROADMAP,
    onViewSettingsUpdate,
    i18nLocale,
  }),
  i18nLocale,
});

export const toRoadmapViewProps = (
  viewSettings: RoadmapViewSettings,
  workspaceId: ArdoqId,
  enhancedScopeData: EnhancedScopeData,
  graphHierarchy: Tree<ArdoqId>[],
  locale: Locale,
  i18nLocale: I18nLocale
): RoadmapViewProps => {
  const workspace = enhancedScopeData.workspacesById[workspaceId];

  if (!workspace) {
    return emptyProps([], viewSettings, i18nLocale);
  }

  const workspaceFields = enhancedScopeData.fields.filter(
    field => field.model === workspace.componentModel
  );
  const fieldOptions = getFieldOptionsForRoadmap(
    workspaceFields,
    viewSettings.selectedFieldName,
    locale
  );

  const selectedField = viewSettings.selectedFieldName
    ? workspaceFields.find(
        field => field.name === viewSettings.selectedFieldName
      )
    : undefined;

  if (!selectedField) {
    return emptyProps(fieldOptions, viewSettings, i18nLocale);
  }

  const fieldValues = fieldOperations.getListFieldOptions(selectedField);

  const hierarchy = buildHierarchyFromScopeData(
    enhancedScopeData,
    graphHierarchy
  );

  const allChildren = hierarchy.flatMap(getChildComponents);

  const legend = buildLegendItems(allChildren, enhancedScopeData);

  const tableRows = hierarchy
    .map(parentItem =>
      buildTableRow(parentItem, fieldValues, selectedField, enhancedScopeData)
    )
    .filter(isTruthy);

  return {
    selectedFieldName: viewSettings.selectedFieldName,
    selectedFieldLabel: selectedField?.label,
    selectedFieldValues: selectedField
      ? fieldOperations.getListFieldOptions(selectedField)
      : [],
    fieldOptions,
    tableRows,
    legend,
    rightMenu: getRightMenuConfig({
      isViewpointMode: false,
      viewId: VIEW_ID,
      viewstate: { selectedFieldName: viewSettings.selectedFieldName },
      exports: {},
      withLegend: false,
      knowledgeBaseLink: KnowledgeBaseLink.ROADMAP,
      onViewSettingsUpdate,
      i18nLocale,
    }),
    i18nLocale,
  };
};

const viewModel$ = combineLatest({
  viewSettings: getWorkspaceModeViewSettings$(VIEW_ID, ''),
  fields: fields$,
  context: context$,
  workspaceAndModel: workspaceAndModel$,
  components: components$,
  references: references$,
  locale: locale$,
  i18nLocale: i18nLocale$,
  groupingRules: groupingRules$,
}).pipe(
  map(
    ({
      viewSettings,
      fields,
      context,
      workspaceAndModel,
      components,
      references,
      locale,
      i18nLocale,
      groupingRules,
    }) => {
      const scopeData = toScopeData(
        context.workspaceIds,
        workspaceAndModel,
        fields.models,
        components.models,
        references.models
      );
      return toRoadmapViewProps(
        viewSettings,
        context.workspaceId,
        scopeDataOperations.enhance(scopeData),
        extractHierarchyFromGraph(
          getNodes(context.workspaceId, groupingRules.groupingRules.length > 0)
        ),
        locale,
        i18nLocale
      );
    }
  )
);

export const RoadmapViewContainer = connect(RoadmapView, viewModel$);
